import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "services", label: "Services", testid: "nav-link-services" },
  { id: "offerings", label: "Offerings", testid: "nav-link-offerings" },
  { id: "coverage", label: "Coverage", testid: "nav-link-coverage" },
  { id: "about", label: "About", testid: "nav-link-about" },
  { id: "contact", label: "Contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        scrolled ? "glass border-white/10 py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/logo.png"
            alt="ARK IT ENTERPRISES"
            className="h-11 w-11 rounded-full ring-1 ring-white/20 group-hover:ring-cyan-400/50 transition-all duration-300"
          />
          <div className="text-left leading-tight">
            <span className="block font-display font-800 font-extrabold tracking-wide text-sm sm:text-base text-white">
              ARK IT ENTERPRISES
            </span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-cyan-400">
              IT INFRASTRUCTURE &amp; SOLUTIONS
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-cyan-300 transition-colors duration-300 uppercase"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            data-testid="nav-call-link"
            href="tel:+919619923347"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            <Phone size={14} className="text-cyan-400" />
            <span className="font-mono text-xs tracking-wider">+91 9619923347</span>
          </a>
          <button
            data-testid="nav-get-quote-button"
            onClick={() => go("contact")}
            className="relative px-5 py-2.5 rounded-full font-display font-semibold text-sm text-ark-950 bg-gradient-to-r from-slate-100 via-slate-300 to-slate-400 hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] hover:scale-[1.03] transition-all duration-300"
          >
            Get Instant Quote
          </button>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-200 p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-mono text-xs tracking-[0.2em] text-slate-300 uppercase py-1"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-call-link"
                href="tel:+919619923347"
                className="flex items-center gap-2 text-cyan-300 font-mono text-xs"
              >
                <Phone size={14} /> +91 9619923347
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
